<template>
    <MainContent title="Detalle de indumentaria">
        <template #header></template>

        <div v-if="loading" class="tw:grid tw:grid-cols-1 tw:gap-6">
            <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-100 tw:overflow-hidden">
                <!-- Header azul -->
                <div class="tw:bg-linear-to-r tw:from-blue-600 tw:to-blue-500 tw:px-6 tw:py-4">
                    <SkeletonItem height="tw:h-5" customClass="tw:w-56 tw:rounded" />
                </div>

                <div class="tw:p-6 tw:space-y-5">
                    <!-- Imagen + datos -->
                    <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-6">
                        <!-- Imagen -->
                        <SkeletonItem height="tw:h-40" customClass="tw:w-40 tw:rounded-lg tw:flex-shrink-0" />

                        <!-- Datos en grid 2 columnas -->
                        <div class="tw:flex-1 tw:bg-blue-50 tw:border tw:border-blue-100 tw:rounded-lg tw:p-4">
                            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                                <div v-for="n in 4" :key="'field-sk-' + n" class="tw:flex tw:items-start tw:gap-3">
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-4 tw:rounded tw:flex-shrink-0" />
                                    <div class="tw:flex-1 tw:space-y-1">
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-16 tw:rounded" />
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-24 tw:rounded" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Separador + Tallas -->
                    <div class="tw:space-y-3">
                        <SkeletonItem height="tw:h-px" customClass="tw:w-full" />
                        <SkeletonItem height="tw:h-3" customClass="tw:w-32 tw:rounded" />
                        <div class="tw:flex tw:flex-wrap tw:gap-2">
                            <SkeletonItem v-for="n in 4" :key="'talla-sk-' + n" height="tw:h-7" customClass="tw:w-14 tw:rounded-lg" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="indumentaria" class="tw:grid tw:grid-cols-1 tw:gap-6">
            <!-- Card principal con datos -->
            <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-100 tw:overflow-hidden">
                <div class="tw:bg-linear-to-r tw:from-blue-600 tw:to-blue-500 tw:px-6 tw:py-4">
                    <h3 class="tw:text-base tw:font-semibold tw:text-white tw:tracking-wide tw:flex tw:items-center tw:gap-2">
                        <i class="fas fa-tshirt tw:opacity-80"></i>
                        Información de la indumentaria
                    </h3>
                </div>

                <div class="tw:p-6 tw:space-y-5">
                    <!-- Imagen + datos básicos -->
                    <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-6">
                        <div class="tw:flex-shrink-0 tw:w-40 tw:h-40">
                            <img
                                v-if="indumentaria.imagen_formatted || indumentaria.imagen"
                                :src="indumentaria.imagen_formatted || indumentaria.imagen"
                                class="tw:w-full tw:h-full tw:object-cover tw:rounded-lg tw:border tw:border-gray-200"
                            />
                            <div
                                v-else
                                class="tw:w-full tw:h-full tw:bg-gray-100 tw:rounded-lg tw:border tw:border-gray-200 tw:flex tw:items-center tw:justify-center"
                            >
                                <i class="fas fa-image tw:text-3xl tw:text-gray-300"></i>
                            </div>
                        </div>

                        <div class="tw:flex-1 tw:bg-blue-50 tw:border tw:border-blue-100 tw:rounded-lg tw:p-4">
                            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                                <DataField
                                    icon="fas fa-hashtag"
                                    label="ID"
                                    :value="indumentaria.id"
                                    labelClass="tw:text-blue-400"
                                    valueClass="tw:text-blue-900"
                                />
                                <DataField
                                    icon="fas fa-euro-sign"
                                    label="Precio"
                                    labelClass="tw:text-blue-400"
                                    valueClass="tw:text-blue-900"
                                >
                                    <template v-if="indumentaria.incluida">
                                        <del class="tw:text-gray-400 tw:mr-2">{{ formatPrice(indumentaria.precio) }}</del>
                                        <span class="tw:text-green-600 tw:font-semibold">Incluida (0 €)</span>
                                    </template>
                                    <span v-else class="tw:text-sm tw:font-medium">{{ formatPrice(indumentaria.precio) }}</span>
                                </DataField>
                                <DataField
                                    icon="fas fa-exclamation-circle"
                                    label="Obligatoria"
                                    labelClass="tw:text-blue-400"
                                    valueClass="tw:text-blue-900"
                                >
                                    <span v-if="indumentaria.obligatoria" class="tw:text-blue-600 tw:font-semibold">
                                        <i class="fas fa-check-circle tw:mr-1"></i>Sí
                                    </span>
                                    <span v-else class="tw:text-gray-400">No</span>
                                </DataField>
                                <DataField
                                    icon="fas fa-layer-group"
                                    label="Incluida en categoría"
                                    labelClass="tw:text-blue-400"
                                    valueClass="tw:text-blue-900"
                                >
                                    <span v-if="indumentaria.incluida" class="tw:text-green-600 tw:font-semibold">
                                        <i class="fas fa-check-circle tw:mr-1"></i>Sí
                                    </span>
                                    <span v-else class="tw:text-gray-400">No</span>
                                </DataField>
                                <DataField
                                    v-if="indumentaria.incluida"
                                    icon="fas fa-cubes"
                                    label="Cantidad incluida"
                                    :value="indumentaria.cantidad_incluida"
                                    labelClass="tw:text-blue-400"
                                    valueClass="tw:text-blue-900"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Tallas -->
                    <div v-if="indumentaria.tallasSelect && indumentaria.tallasSelect.length">
                        <div class="tw:border-t tw:border-gray-100 tw:mb-4"></div>
                        <div class="tw:space-y-3">
                            <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wider tw:flex tw:items-center tw:gap-1">
                                <i class="fas fa-ruler"></i> Tallas disponibles
                            </p>
                            <div class="tw:flex tw:flex-wrap tw:gap-2">
                                <span
                                    v-for="talla in indumentaria.tallasSelect"
                                    :key="talla.id"
                                    class="tw:px-3 tw:py-1.5 tw:text-xs tw:font-medium tw:bg-blue-50 tw:text-blue-700 tw:border tw:border-blue-100 tw:rounded-lg"
                                >
                                    {{ talla.nombre }}
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <div v-else class="tw:text-center tw:py-12">
            <p class="tw:text-gray-500">Indumentaria no encontrada.</p>
            <Button
                as="router-link"
                :to="{ name: 'back.bases.indumentarias.index', params: { lista_id: listaId } }"
                variant="ghost"
                customClass="tw:mt-4"
            >
                Volver al listado
            </Button>
        </div>
    </MainContent>
</template>

<script setup>
    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import DataField from '@/modules/admin/ui/DataField.vue';
    import { SkeletonItem } from '@/modules/admin/ui/skeleton';
    import { useIndumentariaShow } from '@/composables/admin/bases/useIndumentariaShow';
    import { useFormatPrice } from '@/composables/common/useFormatPrice';

    const { indumentaria, loading, listaId, indumentariaId } = useIndumentariaShow();
    const formatPrice = useFormatPrice();
</script>
